import React, { useState, useRef, useEffect } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";

// --- Vimeo Video URLs ---
const videos = [
  "https://player.vimeo.com/video/1136051482?h=9c2e70107d", // Coaching (1-on-1)
  "https://player.vimeo.com/video/1136051434?h=756e01bca2", // Feedback (1-on-1)
  "https://player.vimeo.com/video/1136051387?h=adb2fa98d0", // Presentations (1-on-1)
  "https://player.vimeo.com/video/1136377910?h=0de8208ea0", // Other Services (1-on-1)
];

// --- Corporate Video URLs ---
const corporateVideos = [
  "https://player.vimeo.com/video/1136411993", // A World Champion's Guide to Public Speaking
  "https://player.vimeo.com/video/1136402658", // Communicating with Clarity
  "https://player.vimeo.com/video/1136402774", // Speaking Under Pressure
  "https://player.vimeo.com/video/1150830037", // Custom Training
];

// --- Service Data (1-on-1) ---
const servicesDataOneOnOne = [
  {
    id: 1,
    title: "Coaching",
    description: "One-on-One training, coaching and mentorship with a world champion",
    points: [
      "Fully customized for your goals, challenges, and opportunities",
      "Each session provides actionable tools and techniques you can apply immediately",
      "Focus on storytelling, clarity, emotional engagement, persuasion, delivery, and more",
    ],
    hoverVideo: videos[0],
    isCorporate: false,
  },
  {
    id: 2,
    title: "Feedback",
    description: "Get expert feedback before your next big presentation",
    points: [
      "Book as much or as little time as you need",
      "Totally custom - focus on your top priorities",
      "Perfect for improving delivery, refining your message, and sharpening stories",
    ],
    hoverVideo: videos[1],
    isCorporate: false,
  },
  {
    id: 3,
    title: "Presentations",
    description: "Page-to-stage support, taking you from rough notes to standing ovation",
    points: [
      "All-in VIP service combining refining your message, writing your speech, and perfecting your delivery",
      "Proven systems = rapid turnaround times",
      "Perfect for TEDx talks, keynotes, award speeches, and other high-stakes presentations",
    ],
    hoverVideo: videos[2],
    isCorporate: false,
  },
  {
    id: 4,
    title: "Other Services",
    description: "Looking to improve your communication in other areas?",
    points: [
      "1-on-1 support for interpersonal communication, clarity, and confidence",
      "Master high-stakes interviews, persuasion, and influence",
      "Communicate your unique value—from pitches to online profiles",
    ],
    hoverVideo: videos[3],
    isCorporate: false,
  },
];

// --- Service Data (Corporate) ---
const servicesDataCorporate = [
  {
    id: 1,
    title: "A World Champion's Guide to Public Speaking",
    tagline: "Equip your team with the skills to deliver clear, confident, and compelling professional presentations",
    points: [
      "Covers the four pillars of world class presentations: structure, engagement, storytelling, and delivery",
      "Available as a 90 MINUTE lunch-and-learn, or a HALF DAY workshop",
      "Delivered conveniently over Zoom",
    ],
    hoverVideo: corporateVideos[0],
    isCorporate: true,
  },
  {
    id: 2,
    title: "Communicating with Clarity",
    tagline: "Equip your team with the skills to communicate with precision, relevance, and impact",
    points: [
      "Covers four core disciplines of effective communication: prioritization, stakeholder relevance, language discipline, and audience alignment",
      "Available as a 90 MINUTE lunch-and-learn, or a HALF DAY workshop",
      "Delivered conveniently over Zoom",
    ],
    hoverVideo: corporateVideos[1],
    isCorporate: true,
  },
  {
    id: 3,
    title: "Speaking Under Pressure",
    tagline: "Train your team to respond clearly and confidently in unscripted professional situations",
    points: [
      "Covers four critical skills for effective on-the-spot communication: Developing Frameworks, Mind Mapping, Extrapolating, and Reverse Engineering",
      "Available as a 90 MINUTE lunch-and-learn, or a HALF DAY workshop",
      "Delivered conveniently over Zoom",
    ],
    hoverVideo: corporateVideos[2],
    isCorporate: true,
  },
  {
    id: 4,
    title: "Custom Training",
    tagline: "Bespoke group training solutions available",
    points: [
      "Contact us with your goals and priorities",
      "Delivered conveniently over Zoom",
    ],
    hoverVideo: corporateVideos[3],
    isCorporate: true,
  },
];

// --- REUSABLE ROBUST VIDEO COMPONENT ---
const VimeoBackground = ({ videoUrl, title, isActive = false, isHovered = false }) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);

  useEffect(() => {
    if (!window.Vimeo) {
      const script = document.createElement("script");
      script.src = "https://player.vimeo.com/api/player.js";
      script.async = true;
      script.id = "vimeo-script";
      document.body.appendChild(script);
      script.onload = () => setIsReady(true);
    } else {
      setIsReady(true);
    }
  }, []);

  const handleIframeLoad = () => {
    if (!isReady || !iframeRef.current || !window.Vimeo) return;
    if (playerRef.current) return;

    try {
      const player = new window.Vimeo.Player(iframeRef.current);
      playerRef.current = player;

      player.ready().then(() => {
        player.setVolume(0);
        player.setLoop(true);
        // Don't autoplay - wait for hover
      });
    } catch (err) {
      console.error("Vimeo init failed", err);
    }
  };

  useEffect(() => {
    if (isReady && iframeRef.current && !playerRef.current) {
        handleIframeLoad();
    }
  }, [isReady]);

  // Handle play/pause based on hover state
  useEffect(() => {
    if (playerRef.current) {
      if (isHovered || isActive) {
        // On mobile, try to play immediately when active
        playerRef.current.play().catch((e) => {
          // Silently handle autoplay restrictions
        });
        setHasPlayed(true);
      } else if (hasPlayed) {
        // Only pause if video has been played before
        playerRef.current.pause().catch(() => {});
      }
    }
  }, [isHovered, isActive, hasPlayed]);

  // Additional effect for mobile - ensure video plays when active on mobile
  useEffect(() => {
    if (isActive && playerRef.current) {
      // Small delay to ensure player is ready
      const timer = setTimeout(() => {
        if (playerRef.current) {
          playerRef.current.play().catch(() => {
            // Autoplay may be blocked, user interaction required
          });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  // Listen for user interaction events (arrow clicks) to play video on mobile
  useEffect(() => {
    const handlePlayVideo = () => {
      if (isActive && playerRef.current) {
        setTimeout(() => {
          if (playerRef.current) {
            playerRef.current.play().catch(() => {
              // Silently handle if still blocked
            });
          }
        }, 200);
      }
    };
    window.addEventListener('playActiveVideo', handlePlayVideo);
    return () => window.removeEventListener('playActiveVideo', handlePlayVideo);
  }, [isActive]);

  // Build iframe src - on mobile, try autoplay when active
  const shouldAutoplay = isActive && window.innerWidth < 768;
  const iframeSrc = `${videoUrl}${videoUrl.includes('?') ? '&' : '?'}background=1&autoplay=${shouldAutoplay ? 1 : 0}&loop=1&muted=1&playsinline=1&autopause=0&controls=0`;

  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
        <iframe
            ref={iframeRef}
            onLoad={handleIframeLoad}
            src={iframeSrc}
            className="absolute inset-0 w-full h-full pointer-events-none"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            title={title}
            loading="lazy"
            style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "177.77vh", 
                height: "100vh",
                transform: "translate(-50%, -50%)",
                minWidth: "100%",
                minHeight: "100%",
            }}
        />
        <div className="absolute inset-0 bg-black/60 pointer-events-none"></div>
    </div>
  );
};

// --- Hook to detect mobile ---
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);
  return isMobile;
}

// --- Toggle Component ---
function ServiceToggle({ isCorporate, onToggle }) {
  return (
    <div className="flex items-center justify-center gap-4 mb-8">
      <button
        onClick={() => onToggle(false)}
        className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
          !isCorporate
            ? "bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-lg scale-105"
            : "bg-white/10 text-white/70 hover:bg-white/20"
        }`}
      >
        1-on-1
      </button>
      <button
        onClick={() => onToggle(true)}
        className={`px-6 py-2 rounded-full font-semibold text-sm transition-all duration-300 ${
          isCorporate
            ? "bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white shadow-lg scale-105"
            : "bg-white/10 text-white/70 hover:bg-white/20"
        }`}
      >
        Corporate
      </button>
    </div>
  );
}

// --- Main Component ---
export default function Services() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isCorporate, setIsCorporate] = useState(false);
  const [mobileActiveSection, setMobileActiveSection] = useState(0);
  const isMobile = useIsMobile();
  const colors = { lightText: "#F9FAFB" };

  // Listen for toggle state changes from header
  useEffect(() => {
    const handleSetToggle = (event) => {
      setIsCorporate(event.detail.isCorporate);
      setMobileActiveSection(0); // Reset to first section when toggle changes
    };
    window.addEventListener('setServicesToggle', handleSetToggle);
    return () => {
      window.removeEventListener('setServicesToggle', handleSetToggle);
    };
  }, []);

  // Reset to first section when toggle changes locally
  useEffect(() => {
    setMobileActiveSection(0);
  }, [isCorporate]);

  const servicesData = isCorporate ? servicesDataCorporate : servicesDataOneOnOne;

  return (
    <div id="services" className="flex flex-col items-center w-full bg-[#0d0d19]">
      {isMobile ? (
        <>
          <div className="relative w-full bg-[#0d0d19] pt-8 pb-4 flex flex-col items-center shadow-lg z-30">
            <h2 className="text-4xl font-bold text-white mb-3">Our Services</h2>
            <ServiceToggle isCorporate={isCorporate} onToggle={setIsCorporate} />
            {/* Dot Indicators - positioned between toggle and panels */}
            <div className="flex justify-center gap-2 mt-4 mb-2">
              {servicesData.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setMobileActiveSection(index);
                    // User interaction - trigger video play
                    setTimeout(() => {
                      const event = new CustomEvent('playActiveVideo');
                      window.dispatchEvent(event);
                    }, 100);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    index === mobileActiveSection
                      ? "bg-white w-8"
                      : "bg-white/40 w-2"
                  }`}
                  aria-label={`Go to service ${index + 1}`}
                />
              ))}
            </div>
          </div>
          <MobileStickyScroll 
            services={servicesData} 
            colors={colors} 
            isCorporate={isCorporate}
            activeSection={mobileActiveSection}
            setActiveSection={setMobileActiveSection}
          />
        </>
      ) : (
        <>
          <div className="p-4 sm:p-6 w-full flex flex-col items-center mb-4">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">Our Services</h2>
            <ServiceToggle isCorporate={isCorporate} onToggle={setIsCorporate} />
          </div>

          <div
            onMouseLeave={() => setActiveIndex(null)}
            className="flex md:flex-row h-auto h-[100vh] md:h-[550px] xl:h-[600px] w-full max-w-7xl md:rounded-2xl md:overflow-hidden md:shadow-2xl"
          >
            {servicesData.map((service, index) => (
              <ServicePanel
                key={`${service.id}-${isCorporate}`}
                service={service}
                isActive={activeIndex === index}
                onHover={() => setActiveIndex(index)}
                colors={colors}
                isCorporate={isCorporate}
              />
            ))}
          </div>
        </>
      )}
      <div className="h-10 md:h-0"></div>
    </div>
  );
}

// --- MOBILE ARROW NAVIGATION COMPONENT ---
function MobileStickyScroll({ services, colors, isCorporate, activeSection, setActiveSection }) {
  const handlePrevious = () => {
    setActiveSection((prev) => (prev > 0 ? prev - 1 : services.length - 1));
    // User interaction - trigger video play for new panel
    setTimeout(() => {
      const event = new CustomEvent('playActiveVideo');
      window.dispatchEvent(event);
    }, 100);
  };

  const handleNext = () => {
    setActiveSection((prev) => (prev < services.length - 1 ? prev + 1 : 0));
    // User interaction - trigger video play for new panel
    setTimeout(() => {
      const event = new CustomEvent('playActiveVideo');
      window.dispatchEvent(event);
    }, 100);
  };

  return (
    <div className="relative w-full min-h-screen flex flex-col">
      {/* Navigation Arrows */}
      <div className="absolute inset-0 flex items-center justify-between z-20 pointer-events-none px-4">
        <button
          onClick={handlePrevious}
          className="pointer-events-auto w-10 h-10 rounded-full bg-transparent backdrop-blur-none border-0 flex items-center justify-center transition-all hover:bg-white/20 hover:backdrop-blur-sm active:scale-95 group"
          aria-label="Previous service"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5 opacity-60 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] transition-opacity group-hover:opacity-100"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          onClick={handleNext}
          className="pointer-events-auto w-10 h-10 rounded-full bg-transparent backdrop-blur-none border-0 flex items-center justify-center transition-all hover:bg-white/20 hover:backdrop-blur-sm active:scale-95 group"
          aria-label="Next service"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5 opacity-60 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] transition-opacity group-hover:opacity-100"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Service Card */}
      <div className="relative w-full h-screen flex-shrink-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${activeSection}-${isCorporate}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <MobileCard service={services[activeSection]} colors={colors} isCorporate={isCorporate} />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dot Indicators - removed from here, shown in parent */}
    </div>
  );
}

// --- MOBILE CARD COMPONENT ---
function MobileCard({ service, colors, isCorporate }) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { delay: 0.2, staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const displayDescription = isCorporate ? service.tagline : service.description;

  return (
    <div className="relative w-full h-full bg-black flex flex-col p-4 sm:p-6 pb-20">
      <VimeoBackground videoUrl={service.hoverVideo} title={service.title} isActive={true} isHovered={true} />

      <motion.div
        className="relative z-10 h-full w-full flex flex-col justify-center text-white overflow-y-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="flex flex-col space-y-3 px-1 pb-8">
          <motion.h3 variants={itemVariants} className="text-2xl sm:text-3xl font-bold">
            {service.title}
          </motion.h3>

          <motion.div
            variants={itemVariants}
            className="w-20 h-1 rounded-full"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          />

          <motion.p variants={itemVariants} className="text-base sm:text-lg font-semibold italic">
            {displayDescription}
          </motion.p>

          <ul className="list-disc list-outside space-y-2 sm:space-y-3 text-sm sm:text-base font-medium ml-4 marker:text-[#764ba2]">
            {service.points.map((point, i) => (
              <motion.li key={i} variants={itemVariants}>
                {point}
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="w-full flex flex-wrap gap-3 justify-start px-1 mt-8 sm:mt-12 mb-8">
          {isCorporate ? (
            <>
              {service.id !== 4 && (
                <motion.a
                  variants={itemVariants}
                  className="font-semibold text-[0.9rem] py-3 px-6 rounded-full shadow-lg"
                  style={{
                    backgroundImage: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: colors.lightText,
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href={
                    service.id === 1 
                      ? "/media/Public Speaking.pdf" 
                      : service.id === 2 
                      ? "/media/Clear Communication.pdf" 
                      : service.id === 3 
                      ? "/media/Speaking Under Pressure.pdf" 
                      : "#contact"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LEARN MORE
                </motion.a>
              )}
              <motion.a
                variants={itemVariants}
                className="font-semibold text-[0.9rem] py-3 px-6 rounded-full shadow-lg border-2 border-white/30 bg-white/10 backdrop-blur-sm"
                style={{
                  color: colors.lightText,
                }}
                whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.2)" }}
                whileTap={{ scale: 0.95 }}
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const event = new CustomEvent('openContactModal');
                  window.dispatchEvent(event);
                }}
              >
                CONTACT US
              </motion.a>
            </>
          ) : (
            <motion.a
            variants={itemVariants}
            className="font-semibold text-[0.9rem] py-3 px-6 rounded-full shadow-lg"
            style={{
                backgroundImage: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: colors.lightText,
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="https://calendly.com/speak-with-simon/discovery-session"
            target="_blank"
            rel="noopener noreferrer"
            >
            Book Your Free Session
            </motion.a>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// --- DESKTOP PANEL ---
function ServicePanel({ service, isActive, onHover, colors, isCorporate }) {
  const desktopPanelVariants = { inactive: { flex: 1 }, active: { flex: 2.5 } };
  
  // UPDATED: Set to 160px for corporate titles to prevent cutoff.
  const revealHeight = isCorporate ? 160 : 120;
  const desktopContentVariants = {
    inactive: { y: `calc(100% - ${revealHeight}px)` }, 
    active: { y: 0 },
  };
  
  const desktopTextVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { delay: 0.3, duration: 0.5 } },
  };

  const displayDescription = isCorporate ? service.tagline : service.description;

  return (
    <motion.div
      className="relative h-full overflow-hidden cursor-pointer group"
      initial={false}
      animate={isActive ? "active" : "inactive"}
      variants={desktopPanelVariants}
      transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
      onMouseEnter={onHover}
    >
      <div className="absolute inset-0 z-0">
         <VimeoBackground videoUrl={service.hoverVideo} title={service.title} isActive={isActive} isHovered={isActive} />
      </div>

      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80" />

      <motion.div
        className="absolute bottom-0 left-0 right-0 z-20 text-white h-full flex flex-col py-4"
        style={{ overflowY: "hidden" }}
        variants={desktopContentVariants}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        {/* UPDATED: 
            1. h-[160px] for corporate, h-[120px] for 1-on-1 to match reveal area.
            2. pb-6 (24px) padding at bottom. 
            This forces the text UP, preventing it from being cut at the bottom edge.
        */}
        <div className={`${isCorporate ? 'h-[160px]' : 'h-[120px]'} flex flex-col justify-end pb-6 px-6 shrink-0`}>
             <h3 className={`text-2xl sm:text-3xl xl:text-4xl font-bold w-full mb-[2px] ${isCorporate ? 'whitespace-normal leading-tight' : 'whitespace-nowrap'}`}>
                {service.title}
             </h3>
        </div>

        <motion.div
          className="flex flex-col flex-grow px-6 pb-4"
          initial="hidden"
          animate={isActive ? "visible" : "hidden"}
          variants={desktopTextVariants}
        >
          <div className="space-y-3 flex-grow mt-0">
            <div
              className="w-32 h-1 rounded-full"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            />

            <p className="text-base xl:text-lg font-semibold italic">
              {displayDescription}
            </p>

            <ul className="list-disc list-outside space-y-2 text-base xl:text-lg font-semibold ml-5 marker:text-[#764ba2]">
              {service.points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          </div>

          <div className="mt-6 mb-4 flex flex-wrap gap-3">
            {isCorporate ? (
              <>
                {service.id !== 4 && (
                  <motion.a
                    className="font-semibold text-[0.9rem] py-2 px-5 rounded-full shadow-lg inline-block w-fit"
                    style={{
                      backgroundImage: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: colors.lightText,
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={
                      service.id === 1 
                        ? "/media/Public Speaking.pdf" 
                        : service.id === 2 
                        ? "/media/Clear Communication.pdf" 
                        : service.id === 3 
                        ? "/media/Speaking Under Pressure.pdf" 
                        : "#contact"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LEARN MORE
                  </motion.a>
                )}
                <motion.a
                  className="font-semibold text-[0.9rem] py-2 px-5 rounded-full shadow-lg inline-block w-fit border-2 border-white/30 bg-white/10 backdrop-blur-sm"
                  style={{
                    color: colors.lightText,
                  }}
                  whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.2)" }}
                  whileTap={{ scale: 0.95 }}
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    const event = new CustomEvent('openContactModal');
                    window.dispatchEvent(event);
                  }}
                >
                  CONTACT US
                </motion.a>
              </>
            ) : (
            <motion.a
              className="font-semibold text-[0.9rem] py-2 px-5 rounded-full shadow-lg inline-block w-fit"
              style={{
                backgroundImage: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: colors.lightText,
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://calendly.com/speak-with-simon/discovery-session"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book Your Free Session
            </motion.a>
            )}
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
